import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { jobsApi } from "src/api/exporterApi";
import { Flow } from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Job, JobConfiguration } from "src/jobs/types";
import { getUrlToJobPage } from "src/router/urls";

const DELAY_BETWEEN_REQUESTS = 150; // 150ms delay between requests
const RETRY_DELAY_ON_429 = 2000; // 2 second delay on 429

type Props = {
  workspace: WorkspaceDataplane;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchJobsWithRetry = async (
  baseURL: string,
  flowId: string,
  retries = 3,
): Promise<Job[]> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await jobsApi.get<Job[]>("/jobs", {
        baseURL,
        params: { flow_id: flowId, status: null, include_simulations: false },
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429 && attempt < retries - 1) {
        // Wait longer on 429 before retrying
        await sleep(RETRY_DELAY_ON_429 * (attempt + 1));
        continue;
      }
      // For other errors or last retry, return empty array
      return [];
    }
  }
  return [];
};

const fetchAllJobsSequentially = async (
  baseURL: string,
  flowIds: string[],
  onProgress?: (loaded: number, total: number, jobs: Job[]) => void,
): Promise<Job[]> => {
  const allJobs: Job[] = [];

  for (let i = 0; i < flowIds.length; i++) {
    if (i > 0) {
      // Add delay between requests to avoid rate limiting
      await sleep(DELAY_BETWEEN_REQUESTS);
    }

    const jobs = await fetchJobsWithRetry(baseURL, flowIds[i]);
    allJobs.push(...jobs);

    // Report progress after each flow is loaded
    if (onProgress) {
      onProgress(i + 1, flowIds.length, [...allJobs]);
    }
  }

  return allJobs;
};

type JobWithFlow = Job & {
  flowName: string;
  flowSlug: string;
};

const formatConfiguration = (config?: JobConfiguration): string => {
  if (!config) {
    return "Default (1 concurrent, async)";
  }

  if ("execution_concurrency" in config && config.execution_concurrency) {
    return `Concurrency: ${config.execution_concurrency}, Mode: ${config.execution_mode || "async"}`;
  }

  if ("rate_limit" in config && config.rate_limit) {
    return `Rate Limit: ${config.rate_limit}/${config.rate_limit_interval || "SECOND"}, Mode: ${config.execution_mode || "async"}`;
  }

  return "Custom configuration";
};

const formatSchedule = (
  schedule?: string | null,
  timezone?: string | null,
): string => {
  if (!schedule) {
    return "None (manual only)";
  }
  return timezone ? `${schedule} (${timezone})` : schedule;
};

const JobConfigDisplay: React.FC<{ config?: JobConfiguration }> = ({
  config,
}) => {
  const formatted = useMemo(() => formatConfiguration(config), [config]);

  if (!config) {
    return <span className="text-gray-500">{formatted}</span>;
  }

  return (
    <details className="cursor-pointer">
      <summary className="hover:text-indigo-800 text-indigo-600">
        {formatted}
      </summary>
      <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs">
        {JSON.stringify(config, null, 2)}
      </pre>
    </details>
  );
};

const JobsTable: React.FC<{
  jobs: JobWithFlow[];
  orgId: string;
  wsId: string;
}> = ({ jobs, orgId, wsId }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-gray-500 font-inter-normal-12px">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Job Name
            </th>
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Status
            </th>
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Flow
            </th>
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Schedule
            </th>
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Configuration
            </th>
            <th className="px-3 py-2 text-left text-gray-700 font-inter-semibold-12px">
              Runs (Live/Sandbox)
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const jobUrl = getUrlToJobPage(orgId, wsId, job.flow_id, job.id);
            return (
              <tr key={job.id} className="border-b border-gray-100">
                <td className="px-3 py-2">
                  <a
                    className="hover:text-indigo-800 text-indigo-600 hover:underline"
                    href={`/decide${jobUrl}`}
                  >
                    {job.name}
                  </a>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs ${
                      job.status === "active"
                        ? "text-green-800 bg-green-100"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {job.status}
                  </span>
                </td>
                <td className="px-3 py-2">{job.flowName}</td>
                <td className="px-3 py-2">
                  {formatSchedule(job.schedule, job.schedule_timezone)}
                </td>
                <td className="px-3 py-2">
                  <JobConfigDisplay config={job.configuration} />
                </td>
                <td className="px-3 py-2">
                  {job.live_runs_count || 0} / {job.sandbox_runs_count || 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const JobsDebugSection: React.FC<Props> = ({ workspace }) => {
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number;
    total: number;
    partialJobs: Job[];
  } | null>(null);

  const flowsQuery = useFlows({
    workspaceId: workspace.id,
    folderId: undefined,
    disabled: false,
  });

  const flowIds = useMemo(
    () => (flowsQuery.data || []).map((flow: Flow) => flow.id),
    [flowsQuery.data],
  );

  // Stabilize flowIds - only update when all flow pages have been loaded
  const [stableFlowIds, setStableFlowIds] = useState<string[]>([]);

  useEffect(() => {
    if (flowsQuery.isFullyLoaded && flowIds.length > 0) {
      setStableFlowIds(flowIds);
    }
  }, [flowsQuery.isFullyLoaded, flowIds]);

  // Fetch all jobs sequentially with rate limiting and retry on 429
  // Use stable flow IDs to prevent query restarts
  const jobsQuery = useQuery<Job[], Error>({
    queryKey: ["all-jobs", workspace.base_url, stableFlowIds],
    queryFn: () =>
      fetchAllJobsSequentially(
        workspace.base_url!,
        stableFlowIds,
        (loaded, total, jobs) => {
          setLoadingProgress({ loaded, total, partialJobs: jobs });
        },
      ),
    enabled: !!workspace.base_url && stableFlowIds.length > 0,
    staleTime: 30000, // 30 seconds
  });

  // Clear progress when query succeeds or fails
  useEffect(() => {
    if (jobsQuery.isSuccess || jobsQuery.isError) {
      setLoadingProgress(null);
    }
  }, [jobsQuery.isSuccess, jobsQuery.isError]);

  // Helper function to map jobs with flow info
  const mapJobsWithFlowInfo = useCallback(
    (jobs: Job[]): JobWithFlow[] => {
      if (!flowsQuery.data) {
        return [];
      }

      const flowsById = new Map<string, Flow>(
        flowsQuery.data.map((flow: Flow) => [flow.id, flow]),
      );

      return jobs.map((job: Job) => {
        const flow = flowsById.get(job.flow_id);
        return {
          ...job,
          flowName: flow?.name || "Unknown Flow",
          flowSlug: flow?.slug || "",
        };
      });
    },
    [flowsQuery.data],
  );

  // Use partial jobs while loading, or final data when complete
  const allJobsWithFlows = useMemo<JobWithFlow[]>(() => {
    if (loadingProgress?.partialJobs) {
      return mapJobsWithFlowInfo(loadingProgress.partialJobs);
    }
    if (jobsQuery.data && Array.isArray(jobsQuery.data)) {
      return mapJobsWithFlowInfo(jobsQuery.data);
    }
    return [];
  }, [jobsQuery.data, loadingProgress, mapJobsWithFlowInfo]);

  // Separate active and inactive jobs
  const activeJobs = useMemo(
    () => allJobsWithFlows.filter((job) => job.status === "active"),
    [allJobsWithFlows],
  );

  const inactiveJobs = useMemo(
    () => allJobsWithFlows.filter((job) => job.status === "inactive"),
    [allJobsWithFlows],
  );

  const isError = flowsQuery.isError || jobsQuery.isError;

  // Show initial loading state only when we have no data yet
  if (flowsQuery.isLoading) {
    return (
      <div>
        <h1>Jobs</h1>
        <div className="text-gray-500 font-inter-normal-12px">
          Loading flows...
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <h1>Jobs</h1>
        <div className="text-red-500 font-inter-normal-12px">
          Error loading jobs
        </div>
      </div>
    );
  }

  if (allJobsWithFlows.length === 0 && !loadingProgress) {
    return (
      <div>
        <h1>Jobs</h1>
        <div className="text-gray-500 font-inter-normal-12px">
          No jobs found in this workspace
        </div>
      </div>
    );
  }

  const progressPercent = loadingProgress
    ? Math.round((loadingProgress.loaded / loadingProgress.total) * 100)
    : 100;

  const isLoadingJobs = jobsQuery.isLoading || loadingProgress !== null;

  return (
    <div>
      <h1>
        Jobs{" "}
        {isLoadingJobs ? `(loading...)` : `(${allJobsWithFlows.length} total)`}
      </h1>

      {loadingProgress && (
        <div className="mb-4">
          <div className="mb-1 text-gray-500 font-inter-normal-12px">
            Loading {loadingProgress.loaded} of {loadingProgress.total} flows (
            {allJobsWithFlows.length} jobs found so far)
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-indigo-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {activeJobs.length > 0 && (
        <>
          <h2 className="mb-3 mt-4 text-gray-800 font-inter-semibold-14px">
            Active Jobs ({activeJobs.length})
          </h2>
          <JobsTable
            jobs={activeJobs}
            orgId={workspace.organization_id}
            wsId={workspace.id}
          />
        </>
      )}

      {inactiveJobs.length > 0 && (
        <>
          <h2 className="mb-3 mt-4 text-gray-800 font-inter-semibold-14px">
            Inactive Jobs ({inactiveJobs.length})
          </h2>
          <JobsTable
            jobs={inactiveJobs}
            orgId={workspace.organization_id}
            wsId={workspace.id}
          />
        </>
      )}
    </div>
  );
};
